import React, { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { RiErrorWarningFill } from "react-icons/ri";
import axios from "axios";
import axiosInstance from "../../utils/axiosConfig";
import { API_URL } from "../../constant";
import { BiErrorCircle } from "react-icons/bi";
import { TailSpin } from "react-loader-spinner";
import toast from "react-hot-toast";
import { apiQueryKey } from "../../lib/api/queryKeys";
import {
  FAILED_PAYOUTS_PAGE_SIZE,
  useFailedPayoutsPageQuery,
} from "../../lib/api/useFailedPayoutsPageQuery";

type ResolveModalProps = {
  setResolveModal: (open: boolean) => void;
  setResolveConfirmModal: (open: boolean) => void;
  selectedPayouts: string[];
  setSelectedPayouts: React.Dispatch<React.SetStateAction<string[]>>;
  selectedPayoutAmountById: Record<string, number>;
  setSelectedPayoutAmountById: React.Dispatch<
    React.SetStateAction<Record<string, number>>
  >;
  setRefetchPayoutCount: React.Dispatch<React.SetStateAction<number>>;
};

type FailedPayoutRow = Record<string, unknown> & {
  _id?: string;
  payoutAmount?: number;
  user?: { profile?: { firstName?: string; lastName?: string } };
};

function payoutDisplayName(payout: FailedPayoutRow): string {
  const f = payout?.user?.profile?.firstName?.trim() ?? "";
  const l = payout?.user?.profile?.lastName?.trim() ?? "";
  const name = `${f} ${l}`.trim();
  return name || "Unknown user";
}

function payoutAmountNgn(payout: FailedPayoutRow): number {
  const n = Number(payout?.payoutAmount);
  return Number.isFinite(n) ? n : 0;
}

const ResolveModal = ({
  setResolveModal,
  setResolveConfirmModal,
  selectedPayouts,
  setSelectedPayouts,
  selectedPayoutAmountById,
  setSelectedPayoutAmountById,
  setRefetchPayoutCount,
}: ResolveModalProps) => {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [error, setError] = useState("");

  const {
    data,
    isPending,
    isError,
    error: queryError,
    refetch,
    isFetching,
  } = useFailedPayoutsPageQuery(page, true);

  const payouts = data?.payouts ?? [];
  const totalPages = data?.meta.totalPages ?? 1;
  const totalFailedOnServer = data?.meta.totalPayouts ?? 0;

  const pageIds = useMemo(() => {
    const list = data?.payouts ?? [];
    return list
      .map((row) => String((row as FailedPayoutRow)._id ?? ""))
      .filter(Boolean);
  }, [data]);

  const allOnPageSelected =
    pageIds.length > 0 && pageIds.every((id) => selectedPayouts.includes(id));

  const totalSelectedAmount = useMemo(
    () =>
      selectedPayouts.reduce(
        (sum, id) => sum + (selectedPayoutAmountById[id] ?? 0),
        0
      ),
    [selectedPayouts, selectedPayoutAmountById]
  );

  const togglePayout = (row: FailedPayoutRow) => {
    const id = String(row._id ?? "");
    if (!id) return;
    const amount = payoutAmountNgn(row);
    if (selectedPayouts.includes(id)) {
      setSelectedPayouts((prev) => prev.filter((x) => x !== id));
      setSelectedPayoutAmountById((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setSelectedPayouts((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setSelectedPayoutAmountById((prev) => ({ ...prev, [id]: amount }));
    }
  };

  const toggleSelectAllOnPage = () => {
    if (allOnPageSelected) {
      setSelectedPayouts((prev) => prev.filter((id) => !pageIds.includes(id)));
      setSelectedPayoutAmountById((prev) => {
        const next = { ...prev };
        pageIds.forEach((id) => {
          delete next[id];
        });
        return next;
      });
    } else {
      setSelectedPayouts((prev) => {
        const merged = [...prev];
        pageIds.forEach((id) => {
          if (!merged.includes(id)) merged.push(id);
        });
        return merged;
      });
      setSelectedPayoutAmountById((prev) => {
        const next = { ...prev };
        payouts.forEach((row) => {
          const id = String((row as FailedPayoutRow)._id ?? "");
          if (!id) return;
          next[id] = payoutAmountNgn(row as FailedPayoutRow);
        });
        return next;
      });
    }
  };

  const otpMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.post(`${API_URL}api/users/me/request-otp`, {
        purpose: "transaction",
      });
    },
    onSuccess: () => {
      setError("");
      setResolveModal(false);
      setResolveConfirmModal(true);
    },
    onError: (e: unknown) => {
      const msg =
        axios.isAxiosError(e) &&
        e.response?.data &&
        typeof e.response.data === "object" &&
        "message" in e.response.data
          ? String((e.response.data as { message?: string }).message)
          : "Could not request OTP.";
      setError(msg);
    },
  });

  const markPaidMutation = useMutation({
    mutationFn: async () => {
      await axiosInstance.patch(`${API_URL}api/payouts/update-statuses`, {
        payoutIds: selectedPayouts,
        status: "COMPLETED",
      });
    },
    onSuccess: () => {
      toast.success("Payouts marked as completed");
      setSelectedPayouts([]);
      setSelectedPayoutAmountById({});
      void queryClient.invalidateQueries({
        queryKey: apiQueryKey("admin-payouts-failed"),
      });
      setRefetchPayoutCount((p) => p + 1);
      window.setTimeout(() => setResolveModal(false), 1200);
    },
    onError: (e: unknown) => {
      const msg =
        axios.isAxiosError(e) &&
        e.response?.data &&
        typeof e.response.data === "object" &&
        "message" in e.response.data
          ? String((e.response.data as { message?: string }).message)
          : "Update failed.";
      setError(msg);
    },
  });

  const queryErrMessage =
    queryError &&
    axios.isAxiosError(queryError) &&
    queryError.response?.data &&
    typeof queryError.response.data === "object" &&
    "message" in queryError.response.data
      ? String((queryError.response.data as { message?: string }).message)
      : "Could not load failed payouts.";

  return (
    <div
      className="fixed inset-0 z-[140] flex items-end justify-center overflow-y-auto bg-black/70 p-0 backdrop-blur-[2px] sm:items-center sm:p-4 font-manrope"
      role="presentation"
    >
      <button
        type="button"
        aria-label="Close"
        className="absolute inset-0 cursor-default"
        onClick={() => setResolveModal(false)}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="resolve-payouts-title"
        className="relative z-[141] flex max-h-[92vh] w-full max-w-lg flex-col rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-2xl sm:max-h-[90vh] sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[#2C363E] px-4 py-4 sm:px-5">
          <div className="min-w-0">
            <h2
              id="resolve-payouts-title"
              className="font-sora text-lg font-bold text-white sm:text-xl"
            >
              Resolve failed payouts
            </h2>
            <p className="mt-1 text-[12px] text-[#869AA9] leading-snug">
              Page size {FAILED_PAYOUTS_PAGE_SIZE}. Selections are kept when you
              change pages. Use &quot;Select all on page&quot; to grab every row
              on this page, then go to the next page to add more.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setResolveModal(false)}
            className="shrink-0 rounded-xl border border-[#3A4852] bg-[#1C2227] px-3 py-2 text-[12px] font-semibold text-[#E7EBEE] transition hover:border-[#5C6B76] hover:bg-[#273037]"
          >
            Close
          </button>
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-b border-[#2C363E]/80 px-4 py-2 sm:px-5">
          <p className="text-[11px] text-[#5C6B76]">
            <span className="font-semibold text-[#869AA9]">
              {totalFailedOnServer.toLocaleString()}
            </span>{" "}
            failed on server
          </p>
          {isFetching && !isPending ? (
            <span className="text-[11px] text-[#869AA9]">Refreshing…</span>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {isPending ? (
            <div className="flex flex-col items-center justify-center gap-3 py-16">
              <TailSpin color="#0B75C2" height={36} width={36} />
              <p className="text-[13px] text-[#869AA9]">Loading payouts…</p>
            </div>
          ) : isError ? (
            <div className="rounded-xl border border-red-500/35 bg-red-500/[0.08] px-4 py-6 text-center">
              <p className="text-[13px] text-red-100/95">{queryErrMessage}</p>
              <button
                type="button"
                onClick={() => void refetch()}
                className="mt-4 rounded-lg border border-red-500/40 px-4 py-2 text-[12px] font-semibold text-red-200 hover:bg-red-500/15"
              >
                Retry
              </button>
            </div>
          ) : payouts.length === 0 ? (
            <p className="rounded-xl border border-dashed border-[#2C363E] bg-[#161E25]/50 py-12 text-center text-[13px] text-[#869AA9]">
              No failed payouts on this page.
            </p>
          ) : (
            <>
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <button
                  type="button"
                  onClick={toggleSelectAllOnPage}
                  className="rounded-lg border border-[#3A4852] bg-[#1C2227] px-3 py-1.5 text-[11px] font-semibold text-[#E7EBEE] transition hover:border-secondary_button hover:text-secondary_button"
                >
                  {allOnPageSelected
                    ? "Clear page selection"
                    : "Select all on page"}
                </button>
                <span className="text-[11px] tabular-nums text-[#869AA9]">
                  Page {page} / {totalPages}
                </span>
              </div>
              <ul className="space-y-2">
                {payouts.map((payout, index) => {
                  const row = payout as FailedPayoutRow;
                  const id = String(row._id ?? "");
                  return (
                    <li key={id || `payout-row-${index}`}>
                      <label
                        className={`flex cursor-pointer items-center justify-between gap-3 rounded-xl border px-3 py-3.5 transition sm:px-4 ${
                          selectedPayouts.includes(id)
                            ? "border-secondary_button/50 bg-[#1C2227] ring-1 ring-secondary_button/30"
                            : "border-[#2C363E] bg-[#161E25]/60 hover:border-[#3A4852]"
                        }`}
                      >
                        <span className="flex min-w-0 items-center gap-3">
                          <input
                            type="checkbox"
                            checked={selectedPayouts.includes(id)}
                            onChange={() => togglePayout(row)}
                            className="h-4 w-4 shrink-0 rounded border-[#3A4852] text-secondary_button focus:ring-secondary_button"
                          />
                          <span className="truncate text-[14px] font-medium text-white">
                            {payoutDisplayName(row)}
                          </span>
                        </span>
                        <span className="shrink-0 font-sora text-[15px] font-semibold tabular-nums text-[#BEE1FB]">
                          ₦
                          {payoutAmountNgn(row).toLocaleString("en-US", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 border-t border-[#2C363E] px-4 py-3 sm:px-5">
          <button
            type="button"
            disabled={page <= 1 || isPending}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="rounded-lg border border-[#2C363E] px-3 py-2 text-[12px] font-semibold text-[#E7EBEE] transition enabled:hover:border-[#3A4852] enabled:hover:bg-[#1C2227] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Previous
          </button>
          <span className="text-[12px] font-medium text-[#869AA9]">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            disabled={page >= totalPages || isPending}
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            className="rounded-lg border border-[#2C363E] px-3 py-2 text-[12px] font-semibold text-[#E7EBEE] transition enabled:hover:border-[#3A4852] enabled:hover:bg-[#1C2227] disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next
          </button>
        </div>

        <div className="shrink-0 space-y-4 border-t border-[#2C363E] bg-[#13181B] px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-1 rounded-xl border border-[#2C363E] bg-[#0D1114]/80 py-3 text-center">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#869AA9]">
              Selected total ({selectedPayouts.length} payout
              {selectedPayouts.length === 1 ? "" : "s"})
            </span>
            <span className="font-sora text-2xl font-bold tabular-nums text-white sm:text-3xl">
              ₦
              {selectedPayouts.length >= 1
                ? totalSelectedAmount.toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })
                : "0.00"}
            </span>
          </div>

          <div className="flex gap-3 rounded-xl border border-amber-500/25 bg-amber-500/[0.08] p-3 sm:p-4">
            <RiErrorWarningFill
              className="mt-0.5 shrink-0 text-2xl text-amber-400"
              aria-hidden
            />
            <p className="text-[13px] font-medium leading-relaxed text-amber-100/90">
              Confirm you have not already paid these manually. This action
              cannot be reversed.
            </p>
          </div>

          {error ? (
            <div className="flex items-center gap-2 rounded-xl border border-red-500/40 bg-red-500/10 px-3 py-3 text-[13px] font-medium text-red-200">
              <BiErrorCircle className="shrink-0 text-xl" aria-hidden />
              {error}
            </div>
          ) : null}

          <div className="flex flex-col gap-2 sm:flex-row-reverse sm:gap-3">
            <button
              type="button"
              onClick={() => otpMutation.mutate()}
              disabled={
                selectedPayouts.length === 0 ||
                otpMutation.isPending ||
                markPaidMutation.isPending
              }
              className={
                selectedPayouts.length >= 1 &&
                !otpMutation.isPending &&
                !markPaidMutation.isPending
                  ? "flex min-h-[48px] w-full items-center justify-center rounded-xl bg-secondary_button px-4 text-[14px] font-bold text-white transition hover:brightness-110 sm:flex-1"
                  : "flex min-h-[48px] w-full cursor-not-allowed items-center justify-center rounded-xl bg-[#2C363E] px-4 text-[14px] font-bold text-[#5C6B76] sm:flex-1"
              }
            >
              {otpMutation.isPending ? (
                <TailSpin color="#FFFFFF" height={28} width={28} />
              ) : (
                "Proceed with OTP"
              )}
            </button>
            <button
              type="button"
              onClick={() => markPaidMutation.mutate()}
              disabled={
                selectedPayouts.length === 0 ||
                markPaidMutation.isPending ||
                otpMutation.isPending
              }
              className={
                selectedPayouts.length >= 1 &&
                !markPaidMutation.isPending &&
                !otpMutation.isPending
                  ? "flex min-h-[48px] w-full items-center justify-center rounded-xl border-2 border-secondary_button px-4 text-[14px] font-bold text-secondary_button transition hover:bg-[#1C2227] sm:flex-1"
                  : "flex min-h-[48px] w-full cursor-not-allowed items-center justify-center rounded-xl border-2 border-[#2C363E] px-4 text-[14px] font-bold text-[#5C6B76] sm:flex-1"
              }
            >
              {markPaidMutation.isPending ? (
                <TailSpin color="#0B84CB" height={28} width={28} />
              ) : (
                "Mark as paid"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResolveModal;
